import i18n from 'i18n';
import { addDays } from 'date-fns';
import { INCOME_INITIAL_VALUES } from 'components/IncomeCalculator/const';
import { STATIC_IMAGE_URLS } from 'modules/core/constants';
import { COLORS } from 'modules/core/theme/colors';
import { getStartOfToday } from 'modules/core/utils';

import { LOAN_VARIABLES_FIELDS_NAMES } from './fieldNames.const';
import { IAvailableDealStructureStatus } from '../types';
import { LOAN_VARIABLES_MODAL_FIELDS } from '../common.const';

export const DEFAULT_DAYS_TO_FIRST_PAYMENT = 30;
export const MIN_DAYS_TO_FIRST_PAYMENT = 7;
export const MAX_DAYS_TO_FIRST_PAYMENT = 51;

export const LOAN_VARIABLES_MODAL_DIALOG_ID = {
  SALES_FEES_CALCULATOR: 'salesFeesCalculator',
  MOUNTLY_INCOME_CALCULATOR: 'mountlyIncomeCalculator',
  NET_TRADE_IN_CALCULATOR: 'netTradeInCalculator',
  BACK_ADDS_CALCULATOR: 'backAddsCalculator',
  UNLOCK_INVENTORY: 'unlockYourInventory',
};

export const DEFAULT_LOAN_VARIABLES_VALUES = {
  loanVariablesForm: {
    price: LOAN_VARIABLES_FIELDS_NAMES.RETAIL_PRICE,
    retailPrice: LOAN_VARIABLES_FIELDS_NAMES.RETAIL_PRICE,
    adjustedPrice: LOAN_VARIABLES_FIELDS_NAMES.ADJUSTED_PRICE,
    mileage: NaN,
    guidebookValue: NaN,
    cashDown: NaN,
    rebates: 0,
    netTradeIn: NaN,
    addOns: NaN,
    vsc: NaN,
    gap: NaN,
    totalSalesFees: NaN,
    taxes: NaN,
    payment: NaN,
    loanTerm: undefined,
    loanTermsList: [],
    apr: NaN,
    totalAmountFinanced: NaN,
    lineThree: NaN,
    monthlyIncome: NaN,
    confirmBook: false,
    applyCpo: false,
    vin: undefined,
    stock: undefined,
    vehicleTier: undefined,
    netTradeInForm: {
      netTradeInTotal: NaN,
      tradeInValue: NaN,
      amountOwnedOnTrade: NaN,
      lienHolder: undefined,
      year: undefined,
      make: undefined,
      model: undefined,
      otherYear: undefined,
      otherMake: undefined,
      otherModel: undefined,
    },
    salesFeesForm: {
      salesFees: NaN,
      salesDocumentFees: NaN,
      titleRegistrationLicense: undefined,
    },
    monthlyIncomeForm: {
      totalIncome: NaN,
      savedIncomes: INCOME_INITIAL_VALUES,
    },
    ltvCategories: [],
    isVerifyDealButtonDisabled: false,
    createAddendum: false,
    afgSubmitDeal: false,
    get contractDate() {
      return getStartOfToday();
    },
    get firstPaymentDate() {
      return addDays(getStartOfToday(), DEFAULT_DAYS_TO_FIRST_PAYMENT);
    },
  },
};

export const ALLY_CPO_GUIDEBOOK = 'Ally CPO';

export const AVAILABLE_DEAL_STRUCTURE_STATUSES: Record<string, IAvailableDealStructureStatus> = {
  IN_POLICY: {
    message: i18n.t('components.loanVariablesModal.availableDealStructureStatuses.inPolicy'),
    imageSrc: STATIC_IMAGE_URLS.icons.inPolicyGreen,
    color: COLORS.GREEN_8,
    tableColor: COLORS.GREEN_8,
  },
  MISSING_OR_INVALID_INFO: {
    message: i18n.t('components.loanVariablesModal.availableDealStructureStatuses.missingOrInvalidInfo'),
    imageSrc: STATIC_IMAGE_URLS.icons.invalidInfoRed,
    color: COLORS.RED_7,
    tableColor: COLORS.GREY_3,
    buttonText: i18n.t('components.loanVariablesModal.buttons.missingInfo'),
  },
  OUT_OF_POLICY: {
    message: i18n.t('components.loanVariablesModal.availableDealStructureStatuses.outOfPolicy'),
    imageSrc: STATIC_IMAGE_URLS.icons.outOfPolicyRed,
    color: COLORS.RED_7,
    tableColor: COLORS.RED_7,
    buttonText: i18n.t('components.loanVariablesModal.availableDealStructureStatuses.outOfPolicy'),
  },
};

export const POLICY_DEAL_STRUCTURE_STATUSES = [
  AVAILABLE_DEAL_STRUCTURE_STATUSES.IN_POLICY,
  AVAILABLE_DEAL_STRUCTURE_STATUSES.OUT_OF_POLICY,
];

export const EMPTY_LTV_CATEGORY = {
  category: ' LTV',
  rate: null,
  payments: null,
  profit: null,
  extra_down_payment: null,
  discount_and_processing_fees: null,
  f_and_i_flat: null,
  max_price: null,
  monthly_payment: null,
  calc_monthly_payment: null,
  ltv_ratio: null,
  customer_rate: null,
  extra_cash_or_discount: null,
  f_e_ltv_inc: null,
  f_e_ltv_exc: null,
};

export const SALESPERSON_ALLOWED_DEAL_STRUCTURE_FIELDS = new Set([
  LOAN_VARIABLES_MODAL_FIELDS.EXTRA_CASH_OR_DISCOUNT,
  LOAN_VARIABLES_MODAL_FIELDS.CUSTOMER_RATE,
  LOAN_VARIABLES_MODAL_FIELDS.MONTHLY_PAYMENT,
]);

export const CALCULATION_FIELD_NAMES = [
  LOAN_VARIABLES_FIELDS_NAMES.ADD_ONS.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.APPLY_CPO.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.ANNUAL_MILEAGE.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.BALLOON_TOGGLE.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.CASH_DOWN.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.ENGINE.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.AFG_TRIM.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.GAP.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.GUIDEBOOK_VALUE.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERM.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.MILEAGE.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.MONTHLY_INCOME.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.MONTHLY_INCOME_FORM.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.NET_TRADE_IN.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.NET_TRADE_IN_FORM.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.PARTICIPATION.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.PAYMENT.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.REBATES.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.RETAIL_PRICE.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.SALES_FEES_FORM.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.TAXES.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.TOTAL_SALES_FEES.split('.')[1],
  LOAN_VARIABLES_FIELDS_NAMES.VSC.split('.')[1],
];

export const HISTORY_FIELD_NAMES = [
  LOAN_VARIABLES_FIELDS_NAMES.APPLY_CPO,
  LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERM,
  LOAN_VARIABLES_FIELDS_NAMES.ANNUAL_MILEAGE,
  LOAN_VARIABLES_FIELDS_NAMES.ENGINE,
  LOAN_VARIABLES_FIELDS_NAMES.CREATE_ADDENDUM,
  LOAN_VARIABLES_FIELDS_NAMES.AFG_SUBMIT_DEAL,
  LOAN_VARIABLES_FIELDS_NAMES.CONTRACT_DATE,
  LOAN_VARIABLES_FIELDS_NAMES.FIRST_PAYMENT_DATE,
];
